import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { DateTime } from "luxon";

// mui components
import {
	Box,
	Checkbox,
	Drawer,
	IconButton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from "@mui/material";

// mui icons
import CloseIcon from "@mui/icons-material/Close";

// custom components
import Heading3 from "../../../typography/heading-03";
import Heading5 from "../../../typography/heading-05";
import Body2 from "../../../typography/body-02";
import Body3 from "../../../typography/body-03";
import PrimaryButton from "../../../buttons/primary-button";
import SecondaryButton from "../../../buttons/secondary-button";
import LoadingDataModal from "../../../modal/loading-data-modal";
import { uploadFileWithChunking } from "../../../../utils/upload-file-with-chunks";
import { uploadMultipartFile } from "../../../../utils/upload-multipart-file";
import {
	getMimeTypeFromFilename,
	GENERIC_FALLBACK_CONTENT_TYPE,
	GENERIC_FALLBACK_FILE_NAME,
} from "../../../../utils/document-validation";
import {
	isRetryResponse,
	isRetryItem,
	createRetryHandledError,
	isRetryHandledError,
} from "../../../../utils/document-retry-helper";

// api slice
import { useEnhancedConditionsTemplatesMutation } from "../../../../features/enhanced-conditions-templates/fetchEnhancedConditionsTemplatesApiSlice";
import { useEnhancedConditionsTemplateDetailsMutation } from "../../../../features/enhanced-conditions-template-details/fetchEnhancedConditionsTemplateDetailsApiSlice";
import { useEnhancedConditionsEFolderCreationMutation } from "../../../../features/create-enhanced-conditions-efolder/createEnhancedConditionsEfolderApiSlice";
import { useEnhancedConditionsDocumentsListMutation } from "../../../../features/enhanced-conditions-documents_list/fetchEnhancedConditionsDocumentsListApiSlice";
import { useEnhancedConditionsEfolderDocumentsListMutation } from "../../../../features/enhanced_conditions_efolder_documents_list/fetchEnhancedConditionsEfolderDocumentsListApiSlice";
import { useEnhancedConditionsEfolderDocumentsDownloadUrlsListMutation } from "../../../../features/enhanced_conditions_documents_downloadurls/fetchEnhancedConditionsEfolderDocumentsDownloadUrlsListApiSlice";
import { useEnhancedConditionDownloadFileFromDownloadUrlMutation } from "../../../../features/enhanced_conditions_documents_downloadurls/fetchEnhancedConditionsEfolderDocumentDownloadFileFromDownloadUrlApiSlice";
import { useEnhancedConditionAttachmentUploadUrlMutation } from "../../../../features/enhanced-condition-attachment-upload-url/enhanced-condition-attachment-upload-url";
import { useStandardDocumentUploadFileForS3FallbackMutation } from "../../../../features/standard-document-upload-file-for-s3-fallback/standard-document-upload-file-for-s3-fallback";
import { useEnhancedLoanUploadAttachmentForS3FallbackMutation } from "../../../../features/enhanced-loan-upload-attachment-for-s3-fallback/enhanced-loan-upload-attachment-for-s3-fallback";
import { useAttachEnhancedConditionDocumentToUploadUrlMutation } from "../../../../features/attach-enhanced-condition-document-to-uploadurl/attach-enhanced-condition-document-to-uploadurl";
import { useAttachEnhancedConditionDocumentMutation } from "../../../../features/attach-enhanced-condition-document/attach-enhanced-condition-document";
import { useAttachDocumentConditionMutation } from "../../../../features/attach-document-condition/attach-document-condition";
import { useUpdateFieldValuesTpoMutation } from "../../../../features/update-field-value-tpo/updateFieldsApiSlice";
import { selectImpersonatedUser } from "../../../../features/admin/impersonated-user/impersonatedUserSlice";
import {
	selectSelectedOrganization,
	selectOrganizationName,
	selectUserDetails,
} from "../../../../features/auth/authSlice";
import { selectEnhancedConditionStatus } from "../../../../features/enhanced-condition-status/enhancedConditionStatusSlice";
import { getEnhancedConditionsDocumentsList } from "../../../../app/services/enhanced-conditions-documents-list";

// custom styles
import styles from "./index.module.scss";

const normalizeText = (value) => String(value ?? "").trim();

const firstNonEmpty = (...values) => {
	for (const value of values) {
		const text = normalizeText(value);
		if (text) return text;
	}

	return "";
};

const firstPositiveNumber = (...values) => {
	for (const value of values) {
		const numericValue = Number(value);
		if (Number.isFinite(numericValue) && numericValue > 0) {
			return numericValue;
		}
	}

	return 0;
};

const formatDateTime = (value) => {
	if (!value) return "NA";

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "NA";

	return `${date.toLocaleString("en-US", {
		timeZone: "America/New_York",
		month: "short",
		day: "numeric",
		year: "numeric",
		hour: "numeric",
		minute: "2-digit",
	})} ET`;
};

const LOAN_IN_USE_FALLBACK_MESSAGE = `Document Received. \n\n Your document has been saved, but the loan is currently in use by a Logan team member. LoganConnect will automatically add the document once the loan is available.\n\n No action is needed. Please do not upload the document again.\n\n To confirm when your document is available to Logan Operations, please check the document status on the Documents screen.`;

const getDocumentStatusTone = (value) => {
	const normalized = normalizeText(value).toLowerCase();

	if (
		normalized.includes("not linked") ||
		normalized.includes("not-linked") ||
		normalized.includes("unlinked")
	) {
		return "danger";
	}

	if (
		normalized.includes("fulfill") ||
		normalized.includes("received") ||
		normalized.includes("linked") ||
		normalized.includes("uploaded")
	) {
		return "success";
	}

	if (normalized.includes("pending") || normalized.includes("open")) {
		return "danger";
	}

	if (normalized.includes("request")) {
		return "warning";
	}

	return "neutral";
};

const getDocumentMatchIdentifier = (document) =>
	firstNonEmpty(
		document?.id,
		document?.entityId,
		document?.documentId,
		document?.attachmentId,
		document?.uploadKey,
		document?.raw?.id,
		document?.raw?.entityId,
		document?.raw?.documentId,
		document?.raw?.attachmentId
	);

const getDocumentMatchName = (document) =>
	firstNonEmpty(
		document?.fileName,
		document?.title,
		document?.entityName,
		document?.documentName,
		document?.name,
		document?.label,
		document?.raw?.fileName,
		document?.raw?.title,
		document?.raw?.entityName,
		document?.raw?.documentName,
		document?.raw?.name
	);

const isDocumentLinkedToSupportingDocuments = (
	existingDocument,
	supportingDocuments = []
) =>
	supportingDocuments.some((supportingDocument) => {
		const existingIdentifier = normalizeText(
			getDocumentMatchIdentifier(existingDocument)
		);
		const supportingIdentifier = normalizeText(
			getDocumentMatchIdentifier(supportingDocument)
		);

		if (existingIdentifier && supportingIdentifier) {
			return existingIdentifier === supportingIdentifier;
		}

		const existingName = normalizeText(getDocumentMatchName(existingDocument));
		const supportingName = normalizeText(
			getDocumentMatchName(supportingDocument)
		);

		return (
			existingName &&
			supportingName &&
			existingName.toLowerCase() === supportingName.toLowerCase()
		);
	});

const normalizeDocument = (item) => ({
	id:
		item?.id ?? item?.entityId ?? item?.documentId ?? item?.name ?? item?.title,
	title: firstNonEmpty(
		item?.title,
		item?.fileName,
		item?.entityName,
		item?.documentName,
		item?.name,
		item?.label,
		"Untitled Document"
	),
	status: firstNonEmpty(
		item?.loganConnectStatus,
		item?.documentStatus,
		item?.status,
		item?.connectionStatus,
		item?.assignedStatus,
		"Linked"
	),
	uploadDate: firstNonEmpty(
		item?.uploadDate,
		item?.createdDate,
		item?.statusDate,
		item?.updatedDate
	),
	fileSize: firstPositiveNumber(
		item?.fileSize,
		item?.size,
		item?.documentSize,
		item?.raw?.fileSize,
		item?.raw?.size,
		item?.raw?.documentSize
	),
	raw: item,
});

const dedupeById = (items) => {
	const map = new Map();

	items.forEach((item) => {
		const key = String(item?.id ?? item?.title ?? item?.uploadDate ?? "");
		if (!key) return;
		map.set(key, item);
	});

	return Array.from(map.values());
};

const getSelectedFile = (item) => item?.file ?? item?.raw?.file ?? item;

const isConflictError = (error) =>
	error?.status === 409 ||
	error?.originalStatus === 409 ||
	error?.OriginalStatus === 409 ||
	error?.status_code === 409;

const getDocumentUploadMetadata = (document) => {
	const name = firstNonEmpty(
		document?.fileName,
		document?.title,
		document?.entityName,
		document?.documentName,
		document?.name,
		GENERIC_FALLBACK_FILE_NAME
	);

	return {
		name,
		contentType: firstNonEmpty(
			document?.contentType,
			document?.mimeType,
			document?.raw?.contentType,
			document?.raw?.mimeType,
			getMimeTypeFromFilename(name),
			GENERIC_FALLBACK_CONTENT_TYPE
		),
		fileSize: firstPositiveNumber(
			document?.fileSize,
			document?.raw?.fileSize,
			document?.raw?.size,
			document?.raw?.documentSize,
			document?.size,
			document?.documentSize
		),
	};
};

const normalizeFolderName = (value) =>
	String(value ?? "")
		.trim()
		.toLowerCase();

const getDocumentFolderName = (document) =>
	normalizeFolderName(
		document?.title ?? document?.document_name ?? document?.name
	);

const EnhancedConditionLinkExistingDrawer = ({
	open,
	handleCloseDrawer,
	handleOpenProcessingModal,
	handleCloseProcessingModal,
	handleOpenSuccessModal,
	setProcessingText,
	setSuccessText,
	handleOpenCommonErrorModal,
	drawerData = {},
}) => {
	const params = useParams();
	const [enhancedConditionsDocumentsList] =
		useEnhancedConditionsDocumentsListMutation();
	const [enhancedConditionsTemplates] =
		useEnhancedConditionsTemplatesMutation();
	const [enhancedConditionsTemplateDetails] =
		useEnhancedConditionsTemplateDetailsMutation();
	const [enhancedConditionsEFolderCreation] =
		useEnhancedConditionsEFolderCreationMutation();
	const [loadDocuments] = useEnhancedConditionsEfolderDocumentsListMutation();
	const [loadDownloadUrls] =
		useEnhancedConditionsEfolderDocumentsDownloadUrlsListMutation();
	const [downloadFileFromDownloadUrl] =
		useEnhancedConditionDownloadFileFromDownloadUrlMutation();
	const [enhancedConditionAttachmentUploadUrl] =
		useEnhancedConditionAttachmentUploadUrlMutation();
	const [standardDocumentUploadFileForS3Fallback] =
		useStandardDocumentUploadFileForS3FallbackMutation();
	const [enhancedLoanUploadAttachmentForS3Fallback] =
		useEnhancedLoanUploadAttachmentForS3FallbackMutation();
	const [attachEnhancedConditionDocumentToUploadUrl] =
		useAttachEnhancedConditionDocumentToUploadUrlMutation();
	const [attachEnhancedConditionDocument] =
		useAttachEnhancedConditionDocumentMutation();
	const [attachDocumentCondition] = useAttachDocumentConditionMutation();
	const userDetails = useSelector(selectUserDetails);
	const selectedOrganization = useSelector(selectSelectedOrganization);
	const organizationName = useSelector(selectOrganizationName);
	const currentOrganization = selectedOrganization || organizationName;
	const [updateFieldValuesTpo] = useUpdateFieldValuesTpoMutation();
	const impersonatedUser = useSelector(selectImpersonatedUser);
	const enhancedConditionsStatusFromStore = useSelector(
		selectEnhancedConditionStatus
	);
	const enhancedConditionsStatus = String(
		enhancedConditionsStatusFromStore ?? ""
	)
		.trim()
		.toUpperCase();
	const isEnhancedEnabled = enhancedConditionsStatus === "Y";
	const currentLoggedInUserEmail = String(
		impersonatedUser?.email ?? userDetails?.email ?? ""
	).trim();

	const conditionId = drawerData?.conditionId;

	const loanId = firstNonEmpty(
		drawerData?.loanId,
		drawerData?.loanGuid,
		drawerData?.loan_guid,
		drawerData?.condition?.loanId,
		drawerData?.condition?.loanGuid,
		drawerData?.condition?.loan_guid,
		params?.id
	);

	const conditionTitle = firstNonEmpty(
		drawerData?.conditionTitle,
		drawerData?.selectedDocument?.title,
		drawerData?.row?.title,
		"Condition"
	);

	const [documents, setDocuments] = useState([]);
	const [selectedDocumentIds, setSelectedDocumentIds] = useState([]);
	const [searchValue, setSearchValue] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const successModalShownRef = useRef(false);
	const loanInUseFallbackSuccessRef = useRef(false);
	const loanInUseFallbackFileRef = useRef(null);
	const latestRetryContextRef = useRef({});
	const supportingDocuments = Array.isArray(drawerData?.supportingDocuments)
		? drawerData.supportingDocuments
		: [];

	const handleError = useCallback(
		(errorKey, errorData) => {
			console.error(`[EnhancedConditionLinkExisting] ${errorKey}`, errorData);
			handleCloseProcessingModal?.();
			if (
				errorData?.status === "FETCH_ERROR" ||
				errorData?.status === 413 ||
				errorData?.OriginalStatus === 413 ||
				errorData?.originalStatus === 413
			) {
				handleOpenCommonErrorModal?.(errorKey ?? "FILE_SIZE_TOO_LARGE");
				return;
			}

			handleOpenCommonErrorModal?.(errorKey ?? "DOCUMENT_UPLOAD_ERROR");
		},
		[handleCloseProcessingModal, handleOpenCommonErrorModal]
	);

	const showLoanInUseFallbackSuccess = useCallback(() => {
		loanInUseFallbackSuccessRef.current = false;
		if (successModalShownRef.current) {
			return;
		}

		successModalShownRef.current = true;
		handleOpenSuccessModal?.();
		setSuccessText?.(LOAN_IN_USE_FALLBACK_MESSAGE);

		// Document was saved via the S3 fallback - refresh the tracker the same
		// way the "Refresh" button does, so it shows up without a manual click.
		Promise.resolve(drawerData?.onLinkExistingSuccess?.({})).catch(() => {});
		Promise.resolve(drawerData?.onConditionListRefresh?.()).catch(() => {});
	}, [handleOpenSuccessModal, setSuccessText, drawerData]);

	const buildUploadFilesFromSelectedDocuments = useCallback(
		async (selectedDocumentsToUpload) => {
			const resolvedFiles = [];

			for (const selectedDocument of selectedDocumentsToUpload) {
				const attachmentIds = [selectedDocument?.id]
					.filter((value) => value !== undefined && value !== null)
					.map(String);

				if (attachmentIds.length === 0) {
					throw new Error("Selected document attachment id not found");
				}

				const downloadUrlsResponse = await loadDownloadUrls({
					loanGuid: loanId,
					attachmentIds,
				}).unwrap();

				const attachment = downloadUrlsResponse?.data?.attachments?.find(
					(item) =>
						attachmentIds.includes(String(item?.id)) ||
						attachmentIds.includes(String(item?.attachmentId)) ||
						attachmentIds.includes(String(item?.entityId))
				);

				const downloadUrl = attachment?.originalUrls?.[0] ?? attachment?.url;

				if (!downloadUrl) {
					throw new Error("Document download url unavailable");
				}

				const downloadedFile = await downloadFileFromDownloadUrl({
					downloadUrl,
					authorization: attachment?.authorizationHeader,
				}).unwrap();

				const fileName =
					attachment?.title ??
					selectedDocument?.title ??
					selectedDocument?.name ??
					GENERIC_FALLBACK_FILE_NAME;
				const contentType =
					attachment?.contentType ||
					selectedDocument?.contentType ||
					downloadedFile?.type ||
					getMimeTypeFromFilename(fileName) ||
					GENERIC_FALLBACK_CONTENT_TYPE;
				const fileBlob =
					downloadedFile instanceof Blob
						? downloadedFile
						: new Blob([downloadedFile], { type: contentType });

				if (typeof File !== "undefined") {
					const file = new File([fileBlob], fileName, {
						type: fileBlob.type || contentType,
					});

					resolvedFiles.push(
						Object.assign(file, {
							sourceDocument: selectedDocument,
							sourceDocumentId: selectedDocument?.id,
							sourceDocumentFileSize: fileBlob?.size ?? file.size ?? 0,
						})
					);
					continue;
				}

				resolvedFiles.push({
					name: fileName,
					type: fileBlob.type || contentType,
					size: fileBlob?.size ?? 0,
					sourceDocument: selectedDocument,
					sourceDocumentId: selectedDocument?.id,
					sourceDocumentFileSize: fileBlob?.size ?? 0,
					blob: fileBlob,
				});
			}

			return resolvedFiles;
		},
		[downloadFileFromDownloadUrl, loadDownloadUrls, loanId]
	);

	const checkIfFolderExists = useCallback(
		async (selectedLoanId, selectedDocument) => {
			try {
				console.log(
					"[EnhancedConditionLinkExisting] checking eFolder",
					selectedLoanId,
					selectedDocument
				);
				const loanDocuments = await getEnhancedConditionsDocumentsList({
					loanGuid: selectedLoanId,
					apiRequest: enhancedConditionsDocumentsList,
					handleErrorModal: handleError,
					errorKey: "ENHANCED_CONDITION_LOAN_DOCUMENTS_UNAVAILABLE",
				});

				if (!loanDocuments) {
					console.error(
						"[EnhancedConditionLinkExisting] loan documents unavailable"
					);
					return null;
				}

				const selectedFolderName = normalizeFolderName(selectedDocument?.title);
				const matchedFolder = loanDocuments?.data?.find((doc) => {
					const existingFolderName = getDocumentFolderName(doc);

					return (
						existingFolderName === selectedFolderName ||
						existingFolderName.includes(selectedFolderName) ||
						selectedFolderName.includes(existingFolderName)
					);
				});

				if (matchedFolder) {
					console.log(
						"[EnhancedConditionLinkExisting] matched existing eFolder",
						matchedFolder
					);
					return matchedFolder?.id ?? null;
				}

				console.log(
					"[EnhancedConditionLinkExisting] creating eFolder",
					selectedDocument
				);
				const createdFolder = await enhancedConditionsEFolderCreation({
					documentName: selectedDocument?.title,
					description: selectedDocument?.title,
					loanGuid: selectedLoanId,
				}).unwrap();

				return (
					createdFolder?.data?.[0]?.id ??
					createdFolder?.data?.id ??
					createdFolder?.id ??
					createdFolder?.data?.[0]?.entityId ??
					null
				);
			} catch (error) {
				if (isConflictError(error)) {
					const fallbackResponse = await retryLoanInUseFallback({
						documentKind: isEnhancedEnabled ? "enhanced" : "standard",
						loanType: isEnhancedEnabled ? "Enhanced" : "Standard",
						conditionId: selectedDocument?.id,
						conditionName: selectedDocument?.label ?? selectedDocument?.title,
						conditionType: selectedDocument?.conditionType,
						entityId: selectedDocument?.entityId ?? selectedDocument?.id,
						entityType: selectedDocument?.entityType ?? "Document",
						title: selectedDocument?.title ?? selectedDocument?.label,
						uploadedBy: currentLoggedInUserEmail,
					});

					if (fallbackResponse || loanInUseFallbackSuccessRef.current) {
						showLoanInUseFallbackSuccess();
					}

					return null;
				}

				handleError("ENHANCED_CONDITION_EFOLDER_CREATION_FAILED", error);
				return null;
			}
		},
		[
			enhancedConditionsDocumentsList,
			enhancedConditionsEFolderCreation,
			handleError,
			currentLoggedInUserEmail,
			isEnhancedEnabled,
			retryLoanInUseFallback,
			showLoanInUseFallbackSuccess,
		]
	);

	const efolderDetails = useCallback(
		async (selectedDocument) => {
			try {
				console.log(
					"[EnhancedConditionLinkExisting] loading templates",
					selectedDocument
				);

				let documentName = selectedDocument?.title;
				console.log("documentName", documentName);
				if (isEnhancedEnabled) {
					const templatesResponse =
						await enhancedConditionsTemplates().unwrap();
					console.log(
						"[EnhancedConditionLinkExisting] templates response",
						templatesResponse
					);

					const documentTitle = conditionTitle;

					console.log(
						"[EnhancedConditionLinkExisting] matching template for document title",
						conditionTitle
					);
					const matchedTemplate = templatesResponse?.data?.find(
						(item) =>
							documentTitle &&
							item?.title &&
							documentTitle.toLowerCase().includes(item.title.toLowerCase())
					);

					const selectedId = matchedTemplate?.id;

					if (!selectedId) {
						console.error(
							"[EnhancedConditionLinkExisting] template match not found",
							{ documentTitle, templatesResponse }
						);
						handleError("ENHANCED_CONDITION_UPLOAD_FAILED", templatesResponse);
						return null;
					}

					console.log(
						"[EnhancedConditionLinkExisting] loading template details",
						selectedId
					);
					const templateDetails = await enhancedConditionsTemplateDetails({
						id: selectedId,
					}).unwrap();
					console.log(
						"[EnhancedConditionLinkExisting] template details response",
						templateDetails
					);

					const folderNameCondition =
						templateDetails?.data?.connectSettings?.documentOption;

					if (folderNameCondition === "MatchConditionName") {
						return checkIfFolderExists(loanId, selectedDocument);
					}

					console.log(
						"[EnhancedConditionLinkExisting] loading eFolder documents for default mapping"
					);
					const efolderDocuments = await loadDocuments({
						loanGuid: loanId,
					}).unwrap();
					console.log(
						"[EnhancedConditionLinkExisting] eFolder documents response",
						efolderDocuments
					);
					const documentName = efolderDocuments?.data?.find(
						(doc) =>
							doc?.id ===
							templateDetails?.data?.connectSettings?.documentTemplate?.entityId
					)?.name;
				} else {
					return checkIfFolderExists(loanId, {
						title: documentName,
						internalDescription: `Default Document with name ${documentName}`,
					});
				}
			} catch (error) {
				console.error(
					"[EnhancedConditionLinkExisting] template/eFolder resolution failed",
					error
				);
				handleError("ENHANCED_CONDITION_UPLOAD_FAILED", error);
				return null;
			}
		},
		[
			checkIfFolderExists,
			enhancedConditionsTemplates,
			enhancedConditionsTemplateDetails,
			handleError,
			loanId,
			loadDocuments,
		]
	);

	const buildEnhancedUploadBody = (
		fileDetails,
		efolderId,
		selectedDocument
	) => {
		const { name, contentType, fileSize } = getDocumentUploadMetadata(
			selectedDocument ?? fileDetails?.sourceDocument ?? fileDetails
		);

		return {
			file: {
				contentType: fileDetails?.type,
				name: fileDetails?.name,
				size:
					fileSize ||
					firstPositiveNumber(
						fileDetails?.sourceDocumentFileSize,
						fileDetails?.size
					),
			},
			title: fileDetails?.name,
			conditionId: selectedDocument?.id ?? conditionId,
			conditionName: selectedDocument?.title ?? conditionTitle,
			conditionType:
				drawerData?.conditionType ??
				selectedDocument?.conditionType ??
				selectedDocument?.condition_type ??
				"",
			assignTo: {
				entityId: efolderId,
				entityType: "Document",
			},
		};
	};

	const retryConflictWithS3Fallback = useCallback(
		async ({
			conditionId,
			conditionName,
			conditionType,
			entityId,
			entityType,
			title,
			file,
			uploadedBy,
			loanType,
		}) => {
			const selectedFile = getSelectedFile(file);
			const resolvedLoanType = String(
				loanType ?? (isEnhancedEnabled ? "Enhanced" : "Standard")
			).trim();
			const isEnhancedLoan = resolvedLoanType.toLowerCase() === "enhanced";

			if (!selectedFile) {
				handleError(
					"ENHANCED_CONDITION_DOCUMENT_UPLOAD_FAILED",
					new Error("Missing file for S3 fallback retry")
				);
				return null;
			}

			const ENHANCED_S3_FALLBACK_CHUNK_SIZE_BYTES = 59 * 1024 * 1024;

			return uploadFileWithChunking({
				file: selectedFile,
				thresholdBytes: ENHANCED_S3_FALLBACK_CHUNK_SIZE_BYTES,
				chunkSizeBytes: ENHANCED_S3_FALLBACK_CHUNK_SIZE_BYTES,
				uploadPart: async ({ file: uploadFile, meta }) => {
					const formData = new FormData();
					formData.append("title", title || selectedFile?.name || "");
					formData.append("condition_id", conditionId ?? "");
					formData.append("condition_name", conditionName ?? "");
					formData.append("condition_type", conditionType ?? "");
					formData.append("entity_id", entityId ?? "");
					formData.append("entity_type", entityType || "Document");
					formData.append("uploaded_by", uploadedBy ?? "");
					formData.append("file", uploadFile, selectedFile?.name);
					formData.append("loan_guid", loanId);

					if (meta?.isChunked) {
						formData.append("chunk_index", String(meta?.chunkIndex));
						formData.append("total_chunks", String(meta?.totalChunks));
						formData.append("upload_id", meta?.uploadId);
						formData.append("original_file_name", meta?.originalFileName);
						formData.append(
							"original_file_size",
							String(meta?.originalFileSize)
						);
						formData.append("is_chunked", "true");
						formData.append("is_last_chunk", String(meta?.isLastChunk));
					}

					try {
						if (isEnhancedLoan) {
							return await enhancedLoanUploadAttachmentForS3Fallback({
								loanGuid: loanId,
								formData,
							}).unwrap();
						}

						return await standardDocumentUploadFileForS3Fallback({
							loanGuid: loanId,
							formData,
						}).unwrap();
					} catch (error) {
						handleError("ENHANCED_CONDITION_DOCUMENT_UPLOAD_FAILED", error);
						return null;
					}
				},
			});
		},
		[
			enhancedLoanUploadAttachmentForS3Fallback,
			standardDocumentUploadFileForS3Fallback,
			handleError,
			isEnhancedEnabled,
			loanId,
		]
	);

	const rememberRetryContext = useCallback((context = {}) => {
		latestRetryContextRef.current = {
			...latestRetryContextRef.current,
			...context,
		};
	}, []);

	const getCurrentRetryFile = useCallback(
		(retryContext = {}) =>
			getSelectedFile(
				retryContext?.file ??
					latestRetryContextRef.current?.file ??
					loanInUseFallbackFileRef.current
			),
		[]
	);

	const extractFallbackS3Url = (response) => {
		const candidates = [
			response?.data?.s3_url,
			response?.data?.s3Url,
			response?.data?.uploadUrl,
			response?.data?.url,
			response?.s3_url,
			response?.s3Url,
			response?.uploadUrl,
			response?.url,
			response?.data?.[0]?.s3_url,
			response?.data?.[0]?.s3Url,
		];

		const resolvedS3Url = candidates.find((value) =>
			String(value ?? "").trim()
		);

		return String(resolvedS3Url ?? "").trim();
	};

	const uploadEnhancedConditionFileToS3Fallback = useCallback(
		async (
			file,
			{
				conditionId,
				conditionName,
				conditionType,
				entityId,
				entityType,
				title,
			} = {}
		) => {
			const ENHANCED_S3_FALLBACK_CHUNK_SIZE_BYTES = 59 * 1024 * 1024;
			const selectedFile = getSelectedFile(file);

			return uploadFileWithChunking({
				file: selectedFile,
				thresholdBytes: ENHANCED_S3_FALLBACK_CHUNK_SIZE_BYTES,
				chunkSizeBytes: ENHANCED_S3_FALLBACK_CHUNK_SIZE_BYTES,
				uploadPart: async ({ file: uploadFile, meta }) => {
					const formData = new FormData();
					formData.append("title", title || selectedFile?.name || "");
					formData.append("condition_id", conditionId ?? "");
					formData.append("condition_name", conditionName ?? "");
					formData.append("condition_type", conditionType ?? "");
					formData.append("entity_id", entityId ?? "");
					formData.append("entity_type", entityType || "Document");
					formData.append("loan_guid", loanId ?? "");
					formData.append("file", uploadFile, selectedFile?.name);

					if (meta?.isChunked) {
						formData.append("chunk_index", String(meta?.chunkIndex));
						formData.append("total_chunks", String(meta?.totalChunks));
						formData.append("upload_id", meta?.uploadId);
						formData.append("original_file_name", meta?.originalFileName);
						formData.append(
							"original_file_size",
							String(meta?.originalFileSize)
						);
						formData.append("is_chunked", "true");
						formData.append("is_last_chunk", String(meta?.isLastChunk));
					}

					try {
						const response = await enhancedLoanUploadAttachmentForS3Fallback({
							loanGuid: loanId,
							formData,
						}).unwrap();
						loanInUseFallbackSuccessRef.current = true;
						return response;
					} catch (error) {
						handleError("ENHANCED_CONDITION_DOCUMENT_UPLOAD_FAILED", error);
						return null;
					}
				},
			});
		},
		[enhancedLoanUploadAttachmentForS3Fallback, handleError, loanId]
	);

	const uploadStandardFileToS3Fallback = useCallback(
		async (file, selectedConditionIds = [], loanType = "Standard") => {
			const STANDARD_S3_FALLBACK_CHUNK_SIZE_BYTES = 59 * 1024 * 1024;
			const selectedFile = getSelectedFile(file);

			const resolvedConditionIds = Array.isArray(selectedConditionIds)
				? selectedConditionIds.map((id) => String(id).trim()).filter(Boolean)
				: [];

			return uploadFileWithChunking({
				file: selectedFile,
				thresholdBytes: STANDARD_S3_FALLBACK_CHUNK_SIZE_BYTES,
				chunkSizeBytes: STANDARD_S3_FALLBACK_CHUNK_SIZE_BYTES,
				uploadPart: async ({ file: uploadFile, meta }) => {
					const formData = new FormData();
					formData.append("loan_guid", loanId);
					formData.append("file", uploadFile, selectedFile?.name);

					resolvedConditionIds.forEach((id) => {
						formData.append("condition_ids", id);
					});
					formData.append("loan_type", loanType);
					if (resolvedConditionIds.includes(String(conditionId))) {
						formData.append("condition_name", conditionTitle ?? "");
						formData.append("condition_type", drawerData?.conditionType ?? "");
					}

					if (meta?.isChunked) {
						formData.append("chunk_index", String(meta?.chunkIndex));
						formData.append("total_chunks", String(meta?.totalChunks));
						formData.append("upload_id", meta?.uploadId);
						formData.append("original_file_name", meta?.originalFileName);
						formData.append(
							"original_file_size",
							String(meta?.originalFileSize)
						);
						formData.append("is_chunked", "true");
						formData.append("is_last_chunk", String(meta?.isLastChunk));
					}

					try {
						const response = await standardDocumentUploadFileForS3Fallback({
							loanGuid: loanId,
							formData,
						}).unwrap();
						loanInUseFallbackSuccessRef.current = true;
						return response;
					} catch (error) {
						handleError("DOCUMENT_UPLOAD_ERROR", error);
						return null;
					}
				},
			});
		},
		[
			standardDocumentUploadFileForS3Fallback,
			handleError,
			loanId,
			conditionId,
			conditionTitle,
			drawerData?.conditionType,
		]
	);

	async function retryLoanInUseFallback(retryContext = {}) {
		const resolvedLoanType = String(
			retryContext?.loanType ?? (isEnhancedEnabled ? "Enhanced" : "Standard")
		).trim();
		const isEnhancedLoan = resolvedLoanType.toLowerCase() === "enhanced";
		const selectedFile = getCurrentRetryFile(retryContext);

		if (!selectedFile) {
			handleError(
				"DOCUMENT_UPLOAD_ERROR",
				new Error("Missing file for S3 fallback retry")
			);
			return null;
		}

		let fallbackResponse = null;

		if (isEnhancedLoan) {
			fallbackResponse = await uploadEnhancedConditionFileToS3Fallback(
				selectedFile,
				{
					conditionId: retryContext?.conditionId,
					conditionName: retryContext?.conditionName,
					conditionType: retryContext?.conditionType,
					entityId: retryContext?.entityId,
					entityType: retryContext?.entityType ?? "Document",
					title: retryContext?.title ?? selectedFile?.name,
				}
			);
		} else {
			fallbackResponse = await uploadStandardFileToS3Fallback(
				selectedFile,
				Array.isArray(retryContext?.selectedConditionIds)
					? retryContext?.selectedConditionIds
					: [conditionId].filter(Boolean),
				resolvedLoanType || "Standard"
			);
		}

		const s3Url = extractFallbackS3Url(fallbackResponse);

		if (s3Url) {
			rememberRetryContext({
				...retryContext,
				file: selectedFile,
				originalFileName: selectedFile?.name,
				attachmentFilename: selectedFile?.name,
				attachmentSize: selectedFile?.size ?? "",
				contentType: selectedFile?.type ?? "",
				s3Url,
			});
		} else {
			console.log(
				"[EnhancedConditionLinkExisting] S3 fallback returned without s3_url; treating as loan-in-use retry state",
				{
					loanId,
					fileName: selectedFile?.name,
				}
			);
		}

		if (fallbackResponse) {
			loanInUseFallbackSuccessRef.current = true;
		}

		return fallbackResponse;
	}

	const getEnhancedUploadRequest = useCallback(
		async (enhancedUploadBody, selectedFile) => {
			const resolvedFile = getSelectedFile(selectedFile);
			try {
				console.log(
					"[EnhancedConditionLinkExisting] requesting upload url",
					enhancedUploadBody
				);
				const uploadDocumentResponse =
					await enhancedConditionAttachmentUploadUrl({
						loanGuid: loanId,
						file: enhancedUploadBody?.file,
						title: enhancedUploadBody?.title,
						assignTo: enhancedUploadBody?.assignTo,
						user_email: currentLoggedInUserEmail,
					}).unwrap();
				console.log(
					"[EnhancedConditionLinkExisting] upload url response",
					uploadDocumentResponse
				);

				if (isRetryResponse(uploadDocumentResponse)) {
					handleOpenCommonErrorModal?.("DOCUMENT_RETRY_PENDING");
					throw createRetryHandledError();
				}

				const uploadData = uploadDocumentResponse?.data ?? {};
				const uploadUrl = uploadData?.multiChunkRequired
					? uploadData?.multiChunk?.commitUrl
					: uploadData?.uploadUrl || uploadData?.url;
				const uploadAuthorization =
					uploadData?.authorizationHeader ||
					uploadData?.authorization ||
					uploadData?.authHeader ||
					uploadData?.auth ||
					uploadData?.signature;

				if (!uploadUrl) {
					console.error(
						"[EnhancedConditionLinkExisting] upload url missing",
						uploadDocumentResponse
					);
					handleError(
						"ENHANCED_CONDITION_UPLOAD_FAILED",
						uploadDocumentResponse
					);
					return null;
				}

				return {
					uploadUrl,
					uploadAuthorization,
					multiChunkRequired: uploadData?.multiChunkRequired,
					multiChunk: uploadData?.multiChunk,
				};
			} catch (error) {
				throw error;
			}
		},
		[
			conditionId,
			conditionTitle,
			drawerData?.conditionType,
			enhancedConditionAttachmentUploadUrl,
			loanId,
			currentLoggedInUserEmail,
			retryConflictWithS3Fallback,
		]
	);

	const mapDocumentsToConditions = (
		documents,
		efolderId,
		conditionId,
		conditionType
	) => {
		const result = [];

		const conditionMapping = {
			document_id: efolderId,
			attachment_id: efolderId,
			condition_id: conditionId,
			condition_type: conditionType ?? "underwriting",
			attachment_ids: efolderId,
		};

		result.push(conditionMapping);
		return { condition_mapping: result };
	};

	const uploadEnhancedConditionFile = useCallback(
		async ({
			uploadUrl,
			uploadAuthorization,
			multiChunkRequired,
			multiChunk,
			file,
			conditionIdForRetry,
			conditionNameForRetry,
			entityIdForRetry,
			entityTypeForRetry,
			titleForRetry,
		}) => {
			try {
				const selectedFile = getSelectedFile(file);
				console.log("[EnhancedConditionLinkExisting] uploading file", {
					uploadUrl,
					uploadAuthorization,
					multiChunkRequired,
					multiChunk,
					selectedFile,
				});

				if (multiChunkRequired || Array.isArray(multiChunk?.chunkList)) {
					return await uploadMultipartFile({
						file: selectedFile,
						chunkList: multiChunk?.chunkList,
						commitUrl: uploadUrl,
						authorization: uploadAuthorization,
					});
				}

				const uploadResponse = await attachEnhancedConditionDocumentToUploadUrl(
					{
						uploadUrl,
						authorization: uploadAuthorization,
						file: selectedFile,
						user_email: currentLoggedInUserEmail,
						loanGuid: loanId,
						conditionId: conditionIdForRetry,
						conditionName: conditionNameForRetry,
						entityId: entityIdForRetry,
						entityType: entityTypeForRetry,
						title: titleForRetry,
					}
				).unwrap();

				if (isRetryResponse(uploadResponse)) {
					handleOpenCommonErrorModal?.("DOCUMENT_RETRY_PENDING");
					throw createRetryHandledError();
				}

				return uploadResponse;
			} catch (error) {
				if (isRetryHandledError(error)) {
					throw error;
				}
				console.error(
					"[EnhancedConditionLinkExisting] file upload failed",
					error
				);
				handleError("ENHANCED_CONDITION_DOCUMENT_UPLOAD_FAILED", error);
				return null;
			}
		},
		[attachEnhancedConditionDocumentToUploadUrl, handleError, loanId]
	);

	const attachEnhancedConditionDocumentRecord = useCallback(
		async ({
			assignToEfolderId,
			efolderId,
			selectedDocument,
			selectedDocumentConditionType,
			lastUploadResponse = { data: [] },
			conditionType,
		}) => {
			const selectedDocumentId =
				typeof selectedDocument === "string"
					? selectedDocument
					: (selectedDocument?.id ??
						selectedDocument?.entityId ??
						selectedDocument?.value ??
						selectedDocument?.label ??
						"");
			let attachDocumentResponse;

			try {
				if (isEnhancedEnabled) {
					attachDocumentResponse = await attachEnhancedConditionDocument({
						loanGuid: loanId,
						data: [
							{
								entityId: assignToEfolderId ?? efolderId,
								entityType: "Document",
							},
						],
						conditionId: conditionId,
					}).unwrap();
				} else {
					const mappedDocuments = mapDocumentsToConditions(
						lastUploadResponse?.data ?? [],
						efolderId,
						conditionId,
						firstNonEmpty(
							conditionType,
							selectedDocumentConditionType,
							drawerData?.conditionType,
							selectedDocument?.conditionType,
							selectedDocument?.condition_type
						)
					);
					attachDocumentResponse = await attachDocumentCondition({
						loanGuid: params?.id,
						data: mappedDocuments,
					}).unwrap();
				}
				const responseItems = attachDocumentResponse?.data ?? [];

				if (responseItems.some(isRetryItem)) {
					handleOpenCommonErrorModal?.("DOCUMENT_RETRY_PENDING");
					throw createRetryHandledError();
				}

				if (
					responseItems.length === 0 ||
					responseItems.some((item) => item?.status === "failure")
				) {
					console.error(
						"[EnhancedConditionLinkExisting] attach response indicates failure",
						attachDocumentResponse
					);
					handleError(
						"ENHANCED_CONDITION_DOCUMENT_ASSIGNMENT_FAILED",
						attachDocumentResponse
					);
					return false;
				}

				return true;
			} catch (error) {
				if (isRetryHandledError(error)) {
					throw error;
				}
				console.error(
					"[EnhancedConditionLinkExisting] attach document failed",
					error
				);
				handleError("ENHANCED_CONDITION_DOCUMENT_ASSIGNMENT_FAILED", error);
				return false;
			}
		},
		[
			attachDocumentCondition,
			attachEnhancedConditionDocument,
			drawerData?.conditionType,
			handleError,
			loanId,
			conditionId,
			isEnhancedEnabled,
			params?.id,
		]
	);

	const loadExistingDocuments = useCallback(async () => {
		if (!open || !loanId) return;

		setIsLoading(true);

		try {
			const response = await loadDocuments({ loanGuid: loanId }).unwrap();
			const normalized = Array.isArray(response?.data) ? response.data : [];
			setDocuments(dedupeById(normalized.map(normalizeDocument)));
		} catch (error) {
			handleError("ENHANCED_CONDITION_EFOLDER_DOCUMENTS_UNAVAILABLE", error);
		} finally {
			setIsLoading(false);
		}
	}, [handleError, loanId, loadDocuments, open]);

	useEffect(() => {
		if (!open) {
			setDocuments([]);
			setSelectedDocumentIds([]);
			setSearchValue("");
			return;
		}

		loadExistingDocuments();
	}, [loadExistingDocuments, open]);

	useEffect(() => {
		if (!open) return;
		setSelectedDocumentIds([]);
	}, [open, loanId, conditionId]);

	const documentsWithLinkStatus = useMemo(
		() =>
			documents.map((document) => {
				const isLinked = isDocumentLinkedToSupportingDocuments(
					document,
					supportingDocuments
				);

				return {
					...document,
					linkStatus: isLinked ? "Linked" : "Not Linked",
					linkStatusTone: isLinked ? "success" : "danger",
				};
			}),
		[documents, supportingDocuments]
	);

	const filteredDocuments = useMemo(() => {
		const query = searchValue.toLowerCase().trim();
		const visibleDocuments = documentsWithLinkStatus.filter((document) => {
			const rawDocument = document?.raw ?? document;

			return !(
				rawDocument?.isActive === false && rawDocument?.isRemoved === false
			);
		});

		if (!query) return visibleDocuments;

		return visibleDocuments.filter((document) =>
			[
				document?.title,
				document?.linkStatus,
				document?.uploadDate,
				document?.id,
			]
				.filter(Boolean)
				.some((value) => String(value).toLowerCase().includes(query))
		);
	}, [documentsWithLinkStatus, searchValue]);

	const selectedDocuments = useMemo(
		() =>
			documentsWithLinkStatus.filter((document) =>
				selectedDocumentIds.includes(String(document?.id))
			),
		[documentsWithLinkStatus, selectedDocumentIds]
	);

	const allVisibleSelected =
		filteredDocuments.length > 0 &&
		filteredDocuments.every((document) =>
			selectedDocumentIds.includes(String(document?.id))
		);

	const someVisibleSelected =
		filteredDocuments.some((document) =>
			selectedDocumentIds.includes(String(document?.id))
		) && !allVisibleSelected;

	const toggleDocumentSelection = (documentId) => {
		setSelectedDocumentIds((prev) => {
			const normalizedId = String(documentId);
			if (prev.includes(normalizedId)) {
				return prev.filter((id) => id !== normalizedId);
			}

			return [...prev, normalizedId];
		});
	};

	const handleSelectAll = () => {
		if (allVisibleSelected) {
			setSelectedDocumentIds((prev) =>
				prev.filter(
					(id) =>
						!filteredDocuments.some((document) => String(document.id) === id)
				)
			);
			return;
		}

		setSelectedDocumentIds((prev) => {
			const nextIds = filteredDocuments.map((document) => String(document.id));
			return Array.from(new Set([...prev, ...nextIds]));
		});
	};

	const handleSubmit = async () => {
		if (!conditionId || selectedDocuments.length === 0) return;
		successModalShownRef.current = false;
		loanInUseFallbackSuccessRef.current = false;
		let lastSelectedDocumentFile = null;

		const selectedDocumentsSnapshot = selectedDocuments.map((document) => ({
			...document,
		}));

		const selectedConditionDocuments = [
			{ id: conditionId, title: conditionTitle },
		];
		console.log(
			"[EnhancedConditionLinkExisting] all submitting selected documents",
			{
				conditionId,
				selectedDocuments: selectedDocumentsSnapshot,
				selectedConditionDocuments,
			}
		);

		handleCloseDrawer?.();
		handleOpenProcessingModal?.();
		setProcessingText?.("Linking selected documents");
		loanInUseFallbackFileRef.current = null;

		let valuesArr = [
			{ id: "TPO.X4", value: DateTime.now().toFormat("MM-dd-yyyy") },
			{
				id: "CX.LC.DOC.REC.DATE",
				value: DateTime.fromISO(DateTime.now(), {
					zone: "America/New_York",
				}).toFormat("MM-dd-yyyy hh:mm a"),
			},
		];

		try {
			const selectedConditionDocument = selectedConditionDocuments[0];

			const fallbackFileSource = selectedDocuments[0];
			if (fallbackFileSource) {
				try {
					const fallbackFiles = await buildUploadFilesFromSelectedDocuments([
						fallbackFileSource,
					]);
					loanInUseFallbackFileRef.current = Array.isArray(fallbackFiles)
						? fallbackFiles[0]
						: fallbackFiles;
				} catch (prefetchError) {
					console.warn(
						"[EnhancedConditionLinkExisting] fallback file prefetch failed",
						prefetchError
					);
				}
			}

			const efolderId = await efolderDetails(selectedConditionDocument);

			if (!efolderId) {
				if (successModalShownRef.current) {
					// Already handled via the loan-in-use S3 fallback; success modal is already showing.
					return;
				}
				throw new Error("ENHANCED_CONDITION_EFOLDER_CREATION_FAILED");
			}

			console.log("[EnhancedConditionLinkExisting] resolved eFolder id", {
				efolderId,
				selectedConditionDocument,
			});

			for (const selectedDocument of selectedDocuments) {
				const documentId = firstNonEmpty(
					selectedDocument?.id,
					selectedDocument?.entityId,
					selectedDocument?.documentId
				);

				if (!documentId) {
					throw new Error("ENHANCED_CONDITION_DOCUMENT_UPLOAD_FAILED");
				}

				const selectedDocumentFiles =
					await buildUploadFilesFromSelectedDocuments([selectedDocument]);
				const selectedDocumentFile = Array.isArray(selectedDocumentFiles)
					? selectedDocumentFiles[0]
					: selectedDocumentFiles;
				lastSelectedDocumentFile = selectedDocumentFile;

				console.log("[EnhancedConditionLinkExisting] selectedDocumentFile", {
					selectedDocumentFile,
					fileName: selectedDocumentFile?.name,
					fileSize: selectedDocumentFile?.size,
					fileType: selectedDocumentFile?.type,
					isFile:
						typeof File !== "undefined" && selectedDocumentFile instanceof File,
					isBlob:
						typeof Blob !== "undefined" && selectedDocumentFile instanceof Blob,
				});

				if (!selectedDocumentFile) {
					throw new Error("ENHANCED_CONDITION_DOCUMENT_UPLOAD_FAILED");
				}

				const enhancedUploadBody = await buildEnhancedUploadBody(
					selectedDocumentFile,
					efolderId,
					selectedConditionDocument
				);

				const uploadDetails = await getEnhancedUploadRequest(
					enhancedUploadBody,
					selectedDocumentFile
				);

				if (!uploadDetails) {
					throw new Error("ENHANCED_CONDITION_UPLOAD_FAILED");
				}

				const assignToEfolderId =
					enhancedUploadBody?.assignTo?.entityId ?? null;
				console.log("[EnhancedConditionLinkExisting] upload details resolved", {
					uploadDetails,
					resolvedEfolderId: efolderId,
					assignToEfolderId,
					conditionId,
					conditionName: conditionTitle,
				});

				const uploadResponse = await uploadEnhancedConditionFile({
					uploadUrl: uploadDetails.uploadUrl,
					uploadAuthorization: uploadDetails.uploadAuthorization,
					multiChunkRequired: uploadDetails.multiChunkRequired,
					multiChunk: uploadDetails.multiChunk,
					file: selectedDocumentFile,
					conditionIdForRetry: conditionId,
					conditionNameForRetry: conditionTitle,
					entityIdForRetry: assignToEfolderId,
					entityTypeForRetry: "Document",
					titleForRetry: enhancedUploadBody?.title,
				});

				if (!uploadResponse) {
					throw new Error("ENHANCED_CONDITION_DOCUMENT_UPLOAD_FAILED");
				}

				const attached = await attachEnhancedConditionDocumentRecord({
					assignToEfolderId,
					efolderId,
					selectedDocument,
					conditionType: drawerData?.conditionType,
					selectedDocumentConditionType:
						selectedDocument?.conditionType ??
						selectedDocument?.condition_type ??
						"",
					lastUploadResponse: { data: [] },
				});

				if (!attached) {
					throw new Error("ENHANCED_CONDITION_DOCUMENT_ASSIGNMENT_FAILED");
				}
			}

			await updateFieldValuesTpo({
				loan_guid: params?.id,
				field_values: valuesArr,
				selectedOrganization: currentOrganization,
				userDetails,
			}).unwrap();

			await drawerData?.onLinkExistingSuccess?.({
				conditionId,
				documents: selectedDocumentsSnapshot,
			});

			if (!successModalShownRef.current) {
				successModalShownRef.current = true;
				handleOpenSuccessModal?.();
				setSuccessText?.(
					drawerData?.linkSuccessText ?? `Document Linked Successfully`
				);
			}

			setSelectedDocumentIds([]);
		} catch (error) {
			if (isConflictError(error)) {
				const fallbackResponse = await retryConflictWithS3Fallback({
					documentKind: isEnhancedEnabled ? "enhanced" : "standard",
					loanType: isEnhancedEnabled ? "Enhanced" : "Standard",
					conditionId,
					conditionName: conditionTitle,
					conditionType: drawerData?.conditionType ?? "",
					file: lastSelectedDocumentFile,
					uploadedBy: currentLoggedInUserEmail,
				});
				if (fallbackResponse || loanInUseFallbackSuccessRef.current) {
					showLoanInUseFallbackSuccess();
				}
				return;
			}
			if (!isRetryHandledError(error)) {
				handleError("DOCUMENT_UPLOAD_ERROR", error);
			}
			handleCloseProcessingModal();
		} finally {
			handleCloseProcessingModal?.();
		}
	};

	return (
		<>
			<LoadingDataModal open={isLoading} text="Loading existing documents..." />
			<Drawer
				anchor="right"
				open={Boolean(open)}
				onClose={handleCloseDrawer}
				PaperProps={{
					sx: {
						height: "75vh",
						minHeight: "75vh",
						maxHeight: "75vh",
						overflow: "hidden",
						display: "flex",
						width: { xs: "100%", md: "56vw" },
						minWidth: { md: "56vw" },
						maxWidth: { md: "56vw" },
					},
				}}
			>
				<Stack direction="column" className={styles.drawerRoot}>
					<Stack
						direction="row"
						justifyContent="space-between"
						alignItems="flex-start"
					>
						<Stack direction="column" gap={1} className={styles.headerCopy}>
							<Heading3
								text="Link Existing Documents"
								extraClass={styles.drawerTitle}
								fontType="semibold"
							/>
							<Body2
								text={`Select existing documents to link to ${conditionTitle}`}
								extraClass={styles.drawerSubtitle}
							/>
						</Stack>

						<IconButton
							onClick={handleCloseDrawer}
							aria-label="Close link existing drawer"
							className={styles.closeButton}
						>
							<CloseIcon className={styles.closeIcon} />
						</IconButton>
					</Stack>

					<Box className={styles.searchHeader}>
						<Body2
							text={`Showing ${filteredDocuments.length} document${
								filteredDocuments.length === 1 ? "" : "s"
							}`}
							extraClass={styles.sectionHint}
						/>
						<input
							type="search"
							placeholder="Search document, status, or date"
							value={searchValue}
							onChange={(event) => setSearchValue(event.target.value)}
							className={styles.searchInput}
						/>
					</Box>

					<Box className={styles.tableShell}>
						<TableContainer className={styles.tableContainer}>
							<Table stickyHeader aria-label="existing documents table">
								<TableHead>
									<TableRow>
										<TableCell padding="checkbox">
											<Checkbox
												checked={allVisibleSelected}
												indeterminate={someVisibleSelected}
												onChange={handleSelectAll}
												inputProps={{
													"aria-label": "select all existing documents",
												}}
											/>
										</TableCell>
										<TableCell>
											<Heading5 text="Document" fontType="semibold" />
										</TableCell>
										<TableCell>
											<Heading5 text="Status" fontType="semibold" />
										</TableCell>
										<TableCell>
											<Heading5 text="Uploaded" fontType="semibold" />
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{filteredDocuments.length === 0 ? (
										<TableRow>
											<TableCell colSpan={4}>
												<Box className={styles.emptyState}>
													<Body2 text="No existing documents found" />
												</Box>
											</TableCell>
										</TableRow>
									) : (
										filteredDocuments.map((document) => {
											const documentId = String(document?.id);
											const isSelected =
												selectedDocumentIds.includes(documentId);
											const statusTone =
												document?.linkStatusTone ??
												getDocumentStatusTone(document?.linkStatus);

											return (
												<TableRow
													key={documentId}
													hover
													selected={isSelected}
													onClick={() => toggleDocumentSelection(documentId)}
													className={styles.tableRow}
												>
													<TableCell padding="checkbox">
														<Checkbox checked={isSelected} />
													</TableCell>
													<TableCell>
														<Stack direction="column" gap={0.35}>
															<Body2
																text={document?.title}
																extraClass={styles.statusText}
															/>
															<Body3
																text={document?.id}
																extraClass={styles.statusText}
															/>
														</Stack>
													</TableCell>
													<TableCell>
														<Box
															className={`${styles.statusBadge} ${
																styles[
																	`statusBadge${statusTone.charAt(0).toUpperCase()}${statusTone.slice(1)}`
																]
															}`}
														>
															<Body2
																text={document?.linkStatus}
																extraClass={styles.statusText}
															/>
														</Box>
													</TableCell>
													<TableCell>
														<Body2
															text={formatDateTime(document?.uploadDate)}
															extraClass={styles.uploadDate}
														/>
													</TableCell>
												</TableRow>
											);
										})
									)}
								</TableBody>
							</Table>
						</TableContainer>
					</Box>

					<Stack
						direction="row"
						alignItems="center"
						justifyContent="flex-end"
						gap={1.25}
						className={styles.drawerFooter}
					>
						<SecondaryButton
							text="Reset"
							onClick={() => setSelectedDocumentIds([])}
							disabled={selectedDocumentIds.length === 0}
						/>
						<PrimaryButton
							text={`Link Selected${selectedDocuments.length > 0 ? ` (${selectedDocuments.length})` : ""}`}
							onClick={handleSubmit}
							disabled={selectedDocuments.length === 0}
						/>
					</Stack>
				</Stack>
			</Drawer>
		</>
	);
};

export default EnhancedConditionLinkExistingDrawer;
